// Exam tags for the Updates content layer. Scoped to exams we actually have mentors
// for. Slugs are the canonical wire/storage value (must match the backend
// ALLOWED_EXAM_TAGS in apps/functions/src/announcements/util.ts). Labels are matched
// to the mentor directory's exam values so the "Browse {exam} mentors" CTA can deep-link
// into the Home filter via ?exam=<label>.

export const ALLOWED_EXAM_TAGS = [
  'nda', 'cds', 'jee-main', 'jee-advanced', 'neet-ug', 'neet-pg', 'cuet', 'cuet-pg', 'upsc-cse', 'gate',
  'class-10-boards', 'class-12-boards', 'govt-central', 'govt-state', 'misc-exams',
  'cbse', 'ctet', 'fmge', 'dnb', 'kvs', 'ugc-net', 'state-tet',
  // Banking / SSC / Railways / Defence recruitment expansion (visibility flywheel).
  'ssc-cgl', 'ssc-chsl', 'ibps-po', 'ibps-clerk', 'sbi-po', 'rrb-ntpc', 'rrb-group-d',
  'agniveer', 'afcat', 'capf', 'state-psc',
  // Commercial aviation / pilot licensing.
  'cpl',
  // Hotel management / hospitality — NTA runs NCHMCT JEE, and nta.ac.in is already a
  // scraped source, so its notices auto-flow into Updates once this tag exists.
  'nchmct-jee',
  // Law. Was folded into 'misc-exams'; promoted to its own tag so the CLAT exam calendar and
  // its notices can be addressed directly.
  'clat',
  // Sainik School (AISSEE) — NTA-conducted, so its notices already arrive in the scrape.
  'sainik-school',
  // ICAR AIEEA (PG) / AICE-JRF/SRF — NTA-conducted agricultural entrance, exams.nta.nic.in/icar.
  'icar-aieea',
] as const;
export type ExamTag = (typeof ALLOWED_EXAM_TAGS)[number];

export const EXAM_TAG_LABEL: Record<ExamTag, string> = {
  'nda': 'NDA',
  'cds': 'CDS',
  'jee-main': 'JEE Main',
  'jee-advanced': 'JEE Advanced',
  'neet-ug': 'NEET UG',
  'neet-pg': 'NEET PG',
  'cuet': 'CUET',
  'cuet-pg': 'CUET PG',
  'upsc-cse': 'UPSC CSE',
  'gate': 'GATE',
  'class-10-boards': 'Class 10 Boards',
  'class-12-boards': 'Class 12 Boards',
  'govt-central': 'Govt Vacancies (Central)',
  'govt-state': 'Govt Vacancies (State)',
  'misc-exams': 'Misc Exams',
  'cbse': 'CBSE',
  'ctet': 'CTET',
  'fmge': 'FMGE',
  'dnb': 'DNB/DrNB',
  'kvs': 'KVS',
  'ugc-net': 'UGC NET',
  'state-tet': 'State TET',
  'ssc-cgl': 'SSC CGL',
  'ssc-chsl': 'SSC CHSL',
  'ibps-po': 'IBPS PO',
  'ibps-clerk': 'IBPS Clerk',
  'sbi-po': 'SBI PO',
  'rrb-ntpc': 'RRB NTPC',
  'rrb-group-d': 'RRB Group D',
  'agniveer': 'Agniveer',
  'afcat': 'AFCAT',
  'capf': 'CAPF / Police',
  'state-psc': 'State PSC',
  'cpl': 'CPL (Commercial Pilot)',
  'nchmct-jee': 'NCHMCT JEE',
  'clat': 'CLAT',
  'sainik-school': 'Sainik School',
  'icar-aieea': 'ICAR AIEEA',
};

export function examTagLabel(tag: string): string {
  return (EXAM_TAG_LABEL as Record<string, string>)[tag] ?? tag;
}

export const ANNOUNCEMENT_CATEGORIES = [
  'notification', 'admit-card', 'result', 'counselling', 'exam-date', 'eligibility',
  'date-change', 'general', 'other',
] as const;
export type AnnouncementCategory = (typeof ANNOUNCEMENT_CATEGORIES)[number];

// The directory-axis category (mirror of functions announcements/util.ts NOTICE_CATEGORIES).
export const NOTICE_CATEGORIES = ['vacancy', 'school-admission', 'college-admission', 'question-paper', 'teacher-vacancy'] as const;
export type NoticeCategory = (typeof NOTICE_CATEGORIES)[number];
export const NOTICE_CATEGORY_LABEL: Record<NoticeCategory, string> = {
  'vacancy': 'Vacancies',
  'school-admission': 'School Admissions',
  'college-admission': 'College Admissions',
  'question-paper': 'Question Papers & Answer Keys',
  'teacher-vacancy': 'Teacher Vacancies',
};

const CATEGORY_LABEL: Record<string, string> = {
  'notification': 'Notification',
  'admit-card': 'Admit Card',
  'result': 'Result',
  'counselling': 'Counselling',
  'exam-date': 'Exam Date',
  'eligibility': 'Eligibility',
  'date-change': 'Date Change',
  'general': 'Update',
  'other': 'Update',
};

export function categoryLabel(category: string): string {
  return CATEGORY_LABEL[category] ?? 'Update';
}

// Per-category accent colours for the dense /updates hub — a left rail stripe + badge so
// the list scans by type at a glance. { fg } is badge text/stripe, { bg } the badge fill.
const CATEGORY_COLOR: Record<string, { fg: string; bg: string }> = {
  'notification': { fg: '#1e40af', bg: '#dbeafe' },
  'admit-card': { fg: '#b45309', bg: '#fef3c7' },
  'result': { fg: '#15803d', bg: '#dcfce7' },
  'counselling': { fg: '#6d28d9', bg: '#ede9fe' },
  'exam-date': { fg: '#0f766e', bg: '#ccfbf1' },
  'eligibility': { fg: '#4338ca', bg: '#e0e7ff' },
  'date-change': { fg: '#b91c1c', bg: '#fee2e2' },
  'general': { fg: '#475569', bg: '#f1f5f9' },
  'other': { fg: '#475569', bg: '#f1f5f9' },
};

export function categoryColor(category: string): { fg: string; bg: string } {
  return CATEGORY_COLOR[category] ?? CATEGORY_COLOR['other']!;
}

// Link into the mentor directory pre-filtered to this exam.
export function browseMentorsHref(tag: string): string {
  // The directory reads ?exams=<tag-slug> (plural, slug — see mentorFilterState.parseFilters);
  // the old ?exam=<label> was silently ignored, landing users on an UNfiltered home. The
  // #mentors anchor scrolls straight to the live-mentors grid (Home scrolls once loaded).
  return `/?exams=${encodeURIComponent(tag)}#mentors`;
}

// Hashtag form of an exam tag's label: '#' + label with non-alphanumerics stripped
// (e.g. 'JEE Main' → '#JEEMain', 'Class 12 Boards' → '#Class12Boards').
export function examHashtag(tag: string): string {
  return '#' + examTagLabel(tag).replace(/[^A-Za-z0-9]/g, '');
}
